// @givmo/org-console — the Profile page's BLOCKS tab: the block editor (PE-WEB1, plan §1.4 (2)).
//
// An ordered list of the organization's page blocks in the order they appear publicly (owner
// order — plan §1.2 H1), with an add-block menu (story · photo · video · link), per-block edit /
// move / remove, the listed-only counters (20 blocks + 10 links, D-PE-8), and ONE save: the
// id-preserving full-list replace the host's `replacePage` operation performs.
//
// ══ MODERATION IS HONEST HERE (plan §3.6) ═══════════════════════════════════════════════════════
//
// A block a moderator unlisted arrives with its `unlisted_at` + `unlisted_reason`, and this editor
// shows both, verbatim, on the block itself — the badge, the date, the reason — and says plainly
// that editing or saving will not restore it (an edited row keeps its id, so the server keeps it
// unlisted; moderation is never client input). The row stays editable and removable, exactly as
// the server admits; nothing here can relist.
//
// ══ THE SERVER'S REFUSAL IS THE COPY (plan §2.3) ════════════════════════════════════════════════
//
// A 422 names the offending row (`Block N: …`); the sentence lands under that row, verbatim — this
// package never paraphrases a refusal. The client-side bounds in `profile-model.ts` mirror the
// server's so an obviously-invalid draft never spends a request, and they say so in this file's copy.
// The one exception is a save refused because the page changed after this draft was read
// (PE-WEB18, below): its words are the iOS editor's ruled ones (D-PE-45), never the server's.
//
// ══ A SAVE NEVER OVERWRITES A CHANGE IT DID NOT SEE (PE-WEB18; D-PE-46) ═════════════════════════
//
// The draft keeps the page it was read from — its rows (`baseRows`) and its version (`baseVersion`) —
// and every save sends that version back as `expected_version`, ONLY when the read carried one (a
// backend from before the version never receives a field it does not know). The server refuses a save
// whose version is no longer the page's; the bar then says so and offers Reload page in Save's place,
// which reads the page again and keeps every unsaved change the latest page does not contradict
// (`profile-reload.ts`, the iOS editor's merge), naming what it could not keep above the blocks until
// the next save. The base moves ONLY with a page this draft takes in whole — the first read, a save
// it made, a reload, or a fresh page while nothing is being edited — never with a background read or
// a Statistics save while the draft holds changes, which would pair this draft with a page it never saw.

import { useEffect, useMemo, useRef, useState, type Ref } from 'react';
import type {
  ProfilePageImageUploadOut,
  ProfilePageOut,
  ProfilePageReplaceIn,
} from '@givmo/api-client';
import { isGivmoError } from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ErrorMessage,
  Field,
  Icon,
  IconButton,
  Menu,
  Textarea,
  UploadDropzone,
  testIdProp,
  toast,
} from '@givmo/design-system';
import {
  ArrowDown,
  ArrowUp,
  FileText,
  Image as ImageIcon,
  Link2,
  Plus,
  RotateCw,
  ShieldAlert,
  Trash2,
  Video,
} from 'lucide-react';
import { PROFILE_COPY, formatRemovedDate } from './profile-copy';
import {
  IMAGE_CAPTION_MAX,
  LINK_TITLE_MAX,
  LINK_URL_MAX,
  PROFILE_BLOCK_KINDS,
  STORY_BODY_MAX,
  STORY_TITLE_MAX,
  canAddKind,
  countListed,
  draftFromWire,
  isCanonicalEmbedUrl,
  isStaleSaveRefusal,
  videoProviderLabel,
  isUnlisted,
  moveRow,
  newDraftBlock,
  parseBlockError,
  readPageVersion,
  removeRow,
  toReplaceBody,
  updateRow,
  validateDraft,
  withExpectedVersion,
  type DraftBlock,
  type ProfileBlockKind,
  type ProfilePageVocab,
} from './profile-model';
import { reloadDraft, reloadNoticeLine, type ReloadNoticeItem } from './profile-reload';

const COPY = PROFILE_COPY.blocks;

export interface ProfileBlocksEditorProps {
  /** The owner view, as loaded. A save resolves to the new one and re-primes the draft. */
  page: ProfilePageOut;
  /** Whether this person may change the page (the host derives it from the standing read). */
  canEdit: boolean;
  vocab: ProfilePageVocab;
  onSave: (body: ProfilePageReplaceIn) => Promise<ProfilePageOut>;
  /** Reads the owner view again for Reload page (PE-WEB18); rejects with the read's refusal. */
  onReload: () => Promise<ProfilePageOut>;
  onUploadImage: (file: File) => Promise<ProfilePageImageUploadOut>;
  testIdPrefix: string;
}

/** The page a draft is based on: its rows as read, the saved body they make, and its version. */
interface DraftBase {
  rows: DraftBlock[];
  body: string;
  version: string | null;
}

function baseOf(page: ProfilePageOut): DraftBase {
  const rows = draftFromWire(page);
  return { rows, body: JSON.stringify(toReplaceBody(rows)), version: readPageVersion(page) };
}

const KIND_ICONS: Record<ProfileBlockKind, typeof FileText> = {
  story: FileText,
  image: ImageIcon,
  video: Video,
  link: Link2,
};

function kindLabel(kind: string): string {
  return (COPY.kinds as Record<string, string>)[kind] ?? kind;
}

export function ProfileBlocksEditor({
  page,
  canEdit,
  vocab,
  onSave,
  onReload,
  onUploadImage,
  testIdPrefix,
}: ProfileBlocksEditorProps) {
  const prefix = `${testIdPrefix}-blocks`;
  // The page this draft is based on (PE-WEB18): what a save measures dirtiness against, what Reload
  // page measures the person's changes against, and the version every save sends back.
  const [base, setBase] = useState<DraftBase>(() => baseOf(page));
  const [rows, setRows] = useState<DraftBlock[]>(() => base.rows);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [serverError, setServerError] = useState<{ index: number | null; message: string } | null>(
    null,
  );
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [uploadErrors, setUploadErrors] = useState<Record<string, string>>({});
  // A save refused because the page changed after this draft was read: Reload page is offered in
  // Save's place until it runs or the draft changes (the same draft could only be refused again).
  const [stale, setStale] = useState(false);
  const [reloading, setReloading] = useState(false);
  // What the last Reload page could not keep, said above the blocks until the next save.
  const [notice, setNotice] = useState<ReloadNoticeItem[] | null>(null);
  const [reloads, setReloads] = useState(0);
  const noticeRef = useRef<HTMLDivElement>(null);

  const dirty = JSON.stringify(toReplaceBody(rows)) !== base.body;

  /** Take `next` in whole as the draft's page: its rows, and its rows and version as the base. */
  const prime = (next: ProfilePageOut) => {
    const nextBase = baseOf(next);
    setBase(nextBase);
    setRows(nextBase.rows);
  };

  // A fresh page from the host (after a save, or a refetch while nothing is being edited) re-primes
  // the draft; an in-progress edit is never clobbered by a background refetch — and neither is the
  // base, so its version still names the page the edits were made on (PE-WEB18).
  useEffect(() => {
    if (dirty) return;
    prime(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-prime on a NEW page only
  }, [page]);

  // After each reload the note comes into view, wherever the person was on the page.
  useEffect(() => {
    if (reloads === 0) return;
    noticeRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    noticeRef.current?.focus({ preventScroll: true });
  }, [reloads]);

  const problems = useMemo(() => validateDraft(rows), [rows]);
  const counts = countListed(rows);

  // Nothing changes while a save or Reload page is in flight: its answer replaces the draft, so a
  // change made meanwhile would be lost without a word. Every editing control is held (disabled)
  // until the answer — the iOS editor's `canChange` — and the handlers' own guards stay as a
  // backstop.
  const held = saving || reloading;
  const add = (kind: ProfileBlockKind) => {
    if (held) return;
    setSaved(false);
    setStale(false);
    setRows((prev) => (canAddKind(prev, kind) ? [...prev, newDraftBlock(kind)] : prev));
  };
  const patch = (key: string, fields: Partial<DraftBlock>) => {
    if (held) return;
    setSaved(false);
    setStale(false);
    setServerError(null);
    setRows((prev) => updateRow(prev, key, fields));
  };

  // A save waits for a picture still uploading, as Reload page does: the saved page would replace a
  // picture whose upload lands while the save is in flight.
  const canSave = dirty && problems.size === 0 && !saving && uploadingKey === null;
  const save = async () => {
    if (!canSave || stale) return;
    setSaving(true);
    setServerError(null);
    setSaved(false);
    try {
      // The version goes only where the read carried one (`withExpectedVersion`).
      const next = await onSave(withExpectedVersion(toReplaceBody(rows), base.version));
      prime(next);
      setNotice(null);
      setSaved(true);
    } catch (error) {
      if (isStaleSaveRefusal(error)) {
        setStale(true);
      } else {
        // The server's own sentence, verbatim — a `Block N:` prefix lands it under that row.
        const message = isGivmoError(error) ? error.message : COPY.errors.save;
        setServerError(parseBlockError(message));
      }
    } finally {
      setSaving(false);
    }
  };

  // Reload page (PE-WEB18; D-PE-45): read the page again and keep every change of the person's the
  // latest page does not contradict. Nothing is saved. A read that fails keeps the draft and the
  // offer, and says why in a toast. It waits for a picture still uploading, as Save does (the merge
  // reads the draft as it stands when the read starts, so an upload landing during the read would be
  // dropped).
  const canReload = stale && !reloading && uploadingKey === null;
  const reload = async () => {
    if (!canReload) return;
    setReloading(true);
    try {
      const latest = await onReload();
      const merged = reloadDraft(rows, base.rows, latest);
      setBase(baseOf(latest));
      setRows(merged.rows);
      setUploadErrors((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([key]) => merged.personalKeys.has(key))),
      );
      setNotice(merged.notice);
      setReloads((count) => count + 1);
      setStale(false);
      setSaved(false);
      setServerError(null);
    } catch (error) {
      toast.error(isGivmoError(error) ? error.message : COPY.errors.load);
    } finally {
      setReloading(false);
    }
  };

  const upload = async (key: string, file: File) => {
    if (held) return;
    setUploadingKey(key);
    setUploadErrors((prev) => ({ ...prev, [key]: '' }));
    try {
      const result = await onUploadImage(file);
      patch(key, { mediaKey: result.media_key, mediaUrl: result.url ?? '' });
    } catch (error) {
      const message = isGivmoError(error) ? error.message : COPY.errors.save;
      setUploadErrors((prev) => ({ ...prev, [key]: message }));
    } finally {
      setUploadingKey(null);
    }
  };

  const listedCapReached = counts.blocks >= 20;
  const linkCapReached = counts.links >= 10;

  return (
    <section className="flex flex-col gap-16 pt-16" {...testIdProp(prefix)}>
      <div className="flex flex-wrap items-start justify-between gap-12">
        <div className="min-w-0 max-w-[560px]">
          <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
            {COPY.heading}
          </h2>
          <p className="mt-2 font-sans text-s13 tracking-givmo text-text-tertiary">{COPY.lede}</p>
        </div>
        {canEdit && (
          <Menu.Root>
            <Menu.Trigger
              disabled={held}
              render={
                <Button
                  appearance="saas"
                  tone="primary"
                  size="md"
                  leadingIcon={<Icon icon={Plus} size={16} aria-hidden />}
                  testId={`${prefix}-add`}
                />
              }
            >
              {COPY.add.button}
            </Menu.Trigger>
            <Menu.Content saas align="end" testId={`${prefix}-add-menu`}>
              {PROFILE_BLOCK_KINDS.map((kind) => (
                <Menu.Item
                  key={kind}
                  disabled={!canAddKind(rows, kind)}
                  onClick={() => add(kind)}
                  startIcon={<Icon icon={KIND_ICONS[kind]} size={16} aria-hidden />}
                  testId={`${prefix}-add-${kind}`}
                >
                  {COPY.add[kind]}
                </Menu.Item>
              ))}
            </Menu.Content>
          </Menu.Root>
        )}
      </div>

      <p
        className="font-sans text-s13 tracking-givmo text-text-secondary tabular-nums"
        {...testIdProp(`${prefix}-counters`)}
      >
        {COPY.counters(counts.blocks, counts.links)}
        {listedCapReached && <span className="ml-8 text-text-tertiary">{COPY.add.full}</span>}
        {linkCapReached && <span className="ml-8 text-text-tertiary">{COPY.add.linksFull}</span>}
      </p>

      {!canEdit && (
        <p className="font-sans text-s13 text-text-tertiary" {...testIdProp(`${prefix}-read-only`)}>
          {COPY.readOnly}
        </p>
      )}

      {notice !== null && (
        <ReloadedNote ref={noticeRef} items={notice} dirty={dirty} prefix={prefix} />
      )}

      {rows.length === 0 ? (
        <p className="font-sans text-s14 text-text-tertiary" {...testIdProp(`${prefix}-empty`)}>
          {COPY.empty}
        </p>
      ) : (
        <ol className="flex flex-col gap-12">
          {rows.map((row, index) => (
            <BlockRow
              key={row.key}
              row={row}
              index={index}
              count={rows.length}
              canEdit={canEdit}
              vocab={vocab}
              problem={
                serverError !== null && serverError.index === index
                  ? serverError.message
                  : (problems.get(row.key) ?? null)
              }
              uploading={uploadingKey === row.key}
              held={held}
              uploadError={uploadErrors[row.key] || null}
              onPatch={(fields) => patch(row.key, fields)}
              onMove={(delta) => {
                if (held) return;
                setSaved(false);
                setStale(false);
                setRows((prev) => moveRow(prev, row.key, delta));
              }}
              onRemove={() => {
                if (held) return;
                setSaved(false);
                setStale(false);
                setRows((prev) => removeRow(prev, row.key));
              }}
              onUpload={(file) => void upload(row.key, file)}
              prefix={prefix}
            />
          ))}
        </ol>
      )}

      {canEdit && (
        <div className="flex flex-wrap items-center justify-between gap-12 border-t border-border-subtle pt-16">
          {stale ? (
            // The page changed while this draft was edited: what happened, that reloading keeps the
            // person's changes wherever it can, and Reload page in Save's place (D-PE-45).
            <div className="min-w-0 flex-1" aria-live="polite" {...testIdProp(`${prefix}-stale`)}>
              <p className="font-sans text-s13 tracking-givmo text-danger-700">
                {COPY.stale.refused}
              </p>
              <p className="mt-2 font-sans text-s13 tracking-givmo text-text-tertiary">
                {COPY.stale.reloadKeeps}
              </p>
            </div>
          ) : (
            <p className="font-sans text-s13 tracking-givmo text-text-tertiary" aria-live="polite">
              {serverError !== null && serverError.index === null ? (
                <span className="text-danger-700" {...testIdProp(`${prefix}-error`)}>
                  {serverError.message}
                </span>
              ) : saved ? (
                <span {...testIdProp(`${prefix}-saved`)}>{COPY.saved}</span>
              ) : dirty ? (
                <span {...testIdProp(`${prefix}-unsaved`)}>{COPY.unsaved}</span>
              ) : null}
            </p>
          )}
          {stale ? (
            <Button
              appearance="saas"
              tone="primary"
              size="md"
              loading={reloading}
              disabled={!canReload}
              onClick={() => void reload()}
              testId={`${prefix}-reload`}
            >
              {COPY.stale.reload}
            </Button>
          ) : (
            <Button
              appearance="saas"
              tone="primary"
              size="md"
              loading={saving}
              disabled={!canSave}
              onClick={() => void save()}
              testId={`${prefix}-save`}
            >
              {saving ? COPY.saving : COPY.save}
            </Button>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * What Reload page could not keep (D-PE-45's note), above the blocks until the next save: its title,
 * then — when anything was dropped — "While you were editing:" and one line per dropped thing, then,
 * while something is left to save, what to do next. Focusable so a reload can move focus to it.
 */
function ReloadedNote({
  ref,
  items,
  dirty,
  prefix,
}: {
  ref: Ref<HTMLDivElement>;
  items: readonly ReloadNoticeItem[];
  dirty: boolean;
  prefix: string;
}) {
  const copy = COPY.reloaded;
  return (
    <div ref={ref} tabIndex={-1} className="scroll-mt-[120px] outline-none">
      <Card testId={`${prefix}-reloaded`} role="status">
        <CardBody className="flex items-start gap-12">
          <Icon
            icon={RotateCw}
            size={16}
            aria-hidden
            className="mt-2 shrink-0 text-text-tertiary"
          />
          <div className="min-w-0">
            <p className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
              {copy.title}
            </p>
            {items.length > 0 && (
              <>
                <p className="mt-4 font-sans text-s13 tracking-givmo text-text-secondary">
                  {copy.whileEditing}
                </p>
                <ul className="mt-4 flex list-disc flex-col gap-2 pl-20 font-sans text-s13 tracking-givmo text-text-secondary">
                  {items.map((item, index) => (
                    <li key={index} {...testIdProp(`${prefix}-reloaded-line`)}>
                      {reloadNoticeLine(item)}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {dirty && (
              <p
                className="mt-4 font-sans text-s13 tracking-givmo text-text-secondary"
                {...testIdProp(`${prefix}-reloaded-rest`)}
              >
                {copy.everythingElse}
              </p>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

interface BlockRowProps {
  row: DraftBlock;
  index: number;
  count: number;
  canEdit: boolean;
  vocab: ProfilePageVocab;
  problem: string | null;
  uploading: boolean;
  /** A save or Reload page is in flight: every control is held until its answer (PE-WEB18). */
  held: boolean;
  uploadError: string | null;
  onPatch: (fields: Partial<DraftBlock>) => void;
  onMove: (delta: -1 | 1) => void;
  onRemove: () => void;
  onUpload: (file: File) => void;
  prefix: string;
}

function BlockRow({
  row,
  index,
  count,
  canEdit,
  vocab,
  problem,
  uploading,
  held,
  uploadError,
  onPatch,
  onMove,
  onRemove,
  onUpload,
  prefix,
}: BlockRowProps) {
  const unlisted = isUnlisted(row);
  // A removed block is non-editable except delete (plan §1.4): its fields read only, no reorder.
  const readOnly = !canEdit || unlisted;
  const KindIcon = (KIND_ICONS as Record<string, typeof FileText | undefined>)[row.kind];
  return (
    <li>
      <Card
        className={unlisted ? 'border-danger-100' : undefined}
        testId={`${prefix}-block`}
        data-kind={row.kind}
        data-unlisted={unlisted ? 'true' : undefined}
      >
        <CardBody className="flex flex-col gap-12">
          <div className="flex flex-wrap items-center justify-between gap-8">
            <div className="flex items-center gap-8">
              <Badge tone="neutral" testId={`${prefix}-block-kind`}>
                {KindIcon && <Icon icon={KindIcon} size={12} aria-hidden />}
                {kindLabel(row.kind)}
              </Badge>
              <span className="font-sans text-s12 tracking-givmo text-text-tertiary tabular-nums">
                {COPY.row.position(index)}
              </span>
            </div>
            {canEdit && (
              <div className="flex items-center gap-4">
                {!unlisted && (
                  <>
                    <IconButton
                      aria-label={COPY.row.moveUp}
                      size="sm"
                      disabled={held || index === 0}
                      onClick={() => onMove(-1)}
                      testId={`${prefix}-block-up`}
                    >
                      <Icon icon={ArrowUp} size={16} aria-hidden />
                    </IconButton>
                    <IconButton
                      aria-label={COPY.row.moveDown}
                      size="sm"
                      disabled={held || index === count - 1}
                      onClick={() => onMove(1)}
                      testId={`${prefix}-block-down`}
                    >
                      <Icon icon={ArrowDown} size={16} aria-hidden />
                    </IconButton>
                  </>
                )}
                <IconButton
                  aria-label={COPY.row.remove}
                  size="sm"
                  disabled={held}
                  onClick={onRemove}
                  testId={`${prefix}-block-remove`}
                >
                  <Icon icon={Trash2} size={16} aria-hidden />
                </IconButton>
              </div>
            )}
          </div>

          {unlisted && (
            <div
              className="flex flex-col gap-4 rounded-all12 border border-danger-100 bg-danger-50 px-12 py-10"
              role="status"
              {...testIdProp(`${prefix}-block-moderation`)}
            >
              <span className="flex flex-wrap items-center gap-8">
                <Badge tone="danger" dot testId={`${prefix}-block-badge`}>
                  {COPY.moderation.badge}
                </Badge>
                <span className="font-sans text-s12 tracking-givmo text-text-tertiary">
                  {COPY.moderation.removedOn(formatRemovedDate(row.unlistedAt))}
                </span>
              </span>
              <p
                className="font-sans text-s13 tracking-givmo text-text-strong"
                {...testIdProp(`${prefix}-block-reason`)}
              >
                {row.unlistedReason && row.unlistedReason.length > 0
                  ? COPY.moderation.reason(row.unlistedReason)
                  : COPY.moderation.noReason}
              </p>
              <p className="font-sans text-s12 tracking-givmo text-text-secondary">
                {COPY.moderation.stays}{' '}
                <span {...testIdProp(`${prefix}-block-contact`)}>{COPY.moderation.contact}</span>
              </p>
            </div>
          )}

          <BlockFields
            row={row}
            readOnly={readOnly}
            locked={unlisted}
            held={held}
            vocab={vocab}
            uploading={uploading}
            onPatch={onPatch}
            onUpload={onUpload}
            prefix={prefix}
          />

          {(problem !== null || uploadError !== null) && (
            <ErrorMessage testId={`${prefix}-block-error`}>{problem ?? uploadError}</ErrorMessage>
          )}
        </CardBody>
      </Card>
    </li>
  );
}

function BlockFields({
  row,
  readOnly,
  locked,
  held,
  vocab,
  uploading,
  onPatch,
  onUpload,
  prefix,
}: {
  row: DraftBlock;
  readOnly: boolean;
  /** A removed block: its fields render locked (disabled), not merely read-only (plan §1.4). */
  locked: boolean;
  /** A save or Reload page is in flight: the fields are held (disabled) until its answer. */
  held: boolean;
  vocab: ProfilePageVocab;
  uploading: boolean;
  onPatch: (fields: Partial<DraftBlock>) => void;
  onUpload: (file: File) => void;
  prefix: string;
}) {
  switch (row.kind) {
    case 'story':
      return (
        <div className="flex flex-col gap-12">
          {vocab.storyHint && (
            <p
              className="font-sans text-s12 tracking-givmo text-text-tertiary"
              {...testIdProp(`${prefix}-story-hint`)}
            >
              {vocab.storyHint}
            </p>
          )}
          <Field
            label={COPY.fields.storyTitle}
            value={row.title}
            maxLength={STORY_TITLE_MAX}
            readOnly={readOnly}
            disabled={locked || held}
            onChange={(e) => onPatch({ title: e.target.value })}
            testId={`${prefix}-story-title`}
          />
          <Textarea
            label={COPY.fields.storyBody}
            helper={`${COPY.fields.storyBodyHelper} ${row.body.length.toLocaleString()} / ${STORY_BODY_MAX.toLocaleString()}`}
            value={row.body}
            maxLength={STORY_BODY_MAX}
            rows={5}
            readOnly={readOnly}
            disabled={locked || held}
            onChange={(e) => onPatch({ body: e.target.value })}
            testId={`${prefix}-story-body`}
          />
        </div>
      );
    case 'image':
      return (
        <div className="flex flex-wrap items-start gap-16">
          <UploadDropzone
            label={COPY.fields.imageUpload}
            ariaLabel={COPY.fields.imageUpload}
            hint={COPY.fields.imageUploadHint}
            accept="image/png,image/jpeg,image/webp"
            src={row.mediaUrl.length > 0 ? row.mediaUrl : null}
            imageAlt={row.title}
            disabled={readOnly || uploading || held}
            onFile={onUpload}
            size={136}
            testId={`${prefix}-image-upload`}
            inputTestId={`${prefix}-image-input`}
            imageTestId={`${prefix}-image-preview`}
          />
          <div className="min-w-[240px] flex-1">
            <Field
              label={COPY.fields.imageCaption}
              helper={COPY.fields.imageUploadHelper}
              value={row.title}
              maxLength={IMAGE_CAPTION_MAX}
              readOnly={readOnly}
              disabled={locked || held}
              onChange={(e) => onPatch({ title: e.target.value })}
              testId={`${prefix}-image-caption`}
            />
          </div>
        </div>
      );
    case 'video':
      return (
        <div className="flex flex-col gap-4">
          <Field
            label={COPY.fields.videoUrl}
            helper={COPY.fields.videoUrlHelper}
            placeholder="https://www.youtube.com/watch?v=…"
            value={row.mediaUrl}
            readOnly={readOnly}
            disabled={locked || held}
            onChange={(e) => onPatch({ mediaUrl: e.target.value })}
            testId={`${prefix}-video-url`}
          />
          {isCanonicalEmbedUrl(row.mediaUrl) && (
            <div className="flex items-center gap-12">
              {row.posterUrl !== null && (
                <img
                  src={row.posterUrl}
                  alt=""
                  loading="lazy"
                  className="h-[54px] w-[96px] shrink-0 rounded-md object-cover"
                  {...testIdProp(`${prefix}-video-poster`)}
                />
              )}
              <p
                className="font-sans text-s12 tracking-givmo text-text-tertiary"
                {...testIdProp(`${prefix}-video-canonical`)}
              >
                {COPY.fields.videoCanonical} {videoProviderLabel(row.mediaUrl)}
              </p>
            </div>
          )}
        </div>
      );
    case 'link':
      return (
        <div className="flex flex-wrap gap-12">
          <div className="min-w-[200px] flex-1">
            <Field
              label={COPY.fields.linkTitle}
              value={row.title}
              maxLength={LINK_TITLE_MAX}
              readOnly={readOnly}
              disabled={locked || held}
              onChange={(e) => onPatch({ title: e.target.value })}
              testId={`${prefix}-link-title`}
            />
          </div>
          <div className="min-w-[260px] flex-[2]">
            <Field
              label={COPY.fields.linkUrl}
              helper={COPY.fields.linkUrlHelper}
              placeholder="https://"
              value={row.mediaUrl}
              maxLength={LINK_URL_MAX}
              readOnly={readOnly}
              disabled={locked || held}
              onChange={(e) => onPatch({ mediaUrl: e.target.value })}
              testId={`${prefix}-link-url`}
            />
          </div>
        </div>
      );
    default:
      // A kind this build does not know: named, kept, written back unchanged (plan §1.2).
      return (
        <p
          className="flex items-center gap-8 font-sans text-s13 text-text-tertiary"
          {...testIdProp(`${prefix}-unknown-kind`)}
        >
          <Icon icon={ShieldAlert} size={14} aria-hidden />
          {kindLabel(row.kind)}
        </p>
      );
  }
}
